import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Flex,
    SimpleGrid,
    Heading,
    Text,
    Icon,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    VStack,
    useTheme
} from '@chakra-ui/react';
import { AiOutlineDatabase, AiOutlineReload, AiOutlineTable, AiOutlineWarning } from 'react-icons/ai'; 
import { Loading } from '../../../../components/loading';
import StaticButton from '../../../../components/UI/StaticButton';
import Api from '../../../../services/api';

const DatabaseUsageCard = ({ db }) => {
    const theme = useTheme(); 
    const espacoUsadoFormatted = (db.espacoUsadoMB || 0).toLocaleString('pt-BR');

     if (db.error) {
        return (
           <Box 
               bg="surface" p={5} borderRadius="lg" border="1px solid" 
               borderColor="red.500" h="100%" 
               transition="all 0.2s ease-in-out" boxShadow="md" 
               // SEM minH
           >
               <Flex align="center" mb={4}>
                   <Icon as={AiOutlineWarning} boxSize={8} color="red.400" mr={4} /> 
                   <Box minW={0}> 
                       <Heading as="h4" size="md" color="white" isTruncated title={db.nome}>{db.nome}</Heading>
                       <Text fontSize="sm" color="red.400">{db.error || 'Erro ao carregar'}</Text>
                   </Box>
               </Flex>
           </Box>
       );
   }

    return (
        <Box 
            bg="surface" p={5} borderRadius="lg" border="1px solid" 
            borderColor="gray.800" h="100%" 
            // SEM minH
            transition="all 0.2s ease-in-out" boxShadow="md" 
            _hover={{ 
                borderColor: theme.colors.secondary,
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px 0 rgba(2, 234, 117, 0.2)` 
            }}
        >
            <Flex align="center"> 
                <Icon as={AiOutlineDatabase} boxSize={8} color="secondary" mr={4} /> 
                <Box minW={0}> 
                    <Heading as="h4" size="md" color="white" isTruncated title={db.nome}>{db.nome}</Heading>
                    <Text fontSize="sm" color="gray.400">Espaço Usado: {espacoUsadoFormatted} MB</Text>
                </Box>
            </Flex>
        </Box>
    );
};


const MySQLMonitorPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await Api.get('/monitordb/data/mysql'); 
            setData(res.data);
        } catch (error) {
            console.error("Erro ao carregar dados do monitor MySQL:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (loading && !data) return <Loading />;
    if (!data) return (
        <Flex justify="center" align="center" h="80vh" p={6}>
            <Text color="gray.400">Não foi possível carregar os dados de monitoramento.</Text>
        </Flex>
    );
    
    const { cards, grid, lastUpdate } = data;
    const sortedDatabases = [...(cards.databases || [])].sort((a, b) => (b.espacoUsadoMB || 0) - (a.espacoUsadoMB || 0));

    return (
        <VStack spacing={8} align="stretch" p={0}>
            <Flex align="center" justify="space-between" mb={6} borderBottom="1px solid" borderColor="gray.800" pb={4} direction={{ base: 'column', md: 'row' }} gap={4}>
                <Heading as="h1" size="lg" color="white">Monitor do Banco de Dados MySQL</Heading>
                <Flex gap={4} align="center" w={{ base: '100%', md: 'auto' }} justify="space-between">
                    <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                        Atualizado: {new Date(lastUpdate).toLocaleTimeString()}
                    </Text>
                    <StaticButton 
                        icon={AiOutlineReload} 
                        onClick={fetchData} 
                        isLoading={loading}
                        h="2.5rem"
                    >
                        Atualizar
                    </StaticButton>
                </Flex>
            </Flex>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
                {sortedDatabases.map((db, index) => (
                    <DatabaseUsageCard key={index} db={db} />
                ))}
                {sortedDatabases.length === 0 && (
                    <Box bg="surface" p={5} borderRadius="lg" border="1px dashed" borderColor="gray.700" >
                        <Flex align="center" justify="center" h="100%"><Text color="gray.500">Nenhum banco de dados MySQL encontrado.</Text></Flex>
                    </Box>
                )}
            </SimpleGrid>

            <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                <Flex align="center" mb={4}>
                    <Icon as={AiOutlineTable} color="secondary" boxSize={6} mr={3}/>
                    <Heading as="h3" size="md" color="white">Top 50 Tabelas por Uso de Espaço</Heading> 
                </Flex>
                <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th color="secondary" borderBottomColor="gray.700" textAlign="center">#</Th>
                                <Th color="secondary" borderBottomColor="gray.700" textAlign="left">Nome da Tabela</Th>
                                <Th color="secondary" isNumeric borderBottomColor="gray.700" textAlign="center">Registros</Th>
                                <Th color="secondary" isNumeric borderBottomColor="gray.700" textAlign="center">Tamanho Total (MB)</Th>
                                <Th color="secondary" isNumeric borderBottomColor="gray.700" textAlign="center">Índices (MB)</Th> 
                            </Tr>
                        </Thead>
                        <Tbody>
                            {(grid.topTables || []).map((table, index) => (
                                <Tr key={index} _hover={{ bg: 'gray.800' }}>
                                    <Td borderBottomColor="gray.700" width="50px" textAlign="center">{index + 1}</Td>
                                    <Td borderBottomColor="gray.700" maxW="300px" isTruncated title={table.TableName} textAlign="left">{table.TableName}</Td> 
                                    <Td isNumeric borderBottomColor="gray.700" textAlign="center">{(table.RowCounts || 0).toLocaleString('pt-BR')}</Td>
                                    <Td isNumeric borderBottomColor="gray.700" textAlign="center">{parseFloat(table.TotalSpaceMB || 0).toFixed(2)}</Td>
                                    <Td isNumeric borderBottomColor="gray.700" textAlign="center">{parseFloat(table.IndexSpaceMB || 0).toFixed(2)}</Td> 
                                </Tr>
                            ))}
                            {(grid.topTables || []).length === 0 && (
                                <Tr><Td colSpan={5} textAlign="center" color="gray.500" borderBottomColor="gray.700">Nenhuma tabela encontrada.</Td></Tr>
                            )}
                        </Tbody>
                    </Table>
                </Box>
            </Box>
        </VStack>
    );
};

export default MySQLMonitorPanel;